// Mirrors the GabSync API contract (MultiTenancyManagement.API). Enums are strings because the
// backend enums are annotated with JsonStringEnumConverter; property names are camelCase to match
// ASP.NET Core's default JSON policy. Row payload dictionaries keep their original DB column names.

export type RowChangeType = "New" | "Changed" | "Unchanged";

export type RowConflictType = "NullBusinessKey" | "DuplicateTargetKey" | "MissingParent";

export type GabRowResolutionType = "Keep" | "TakeSource" | "Custom";

export type GabSyncRunStatus =
  | "Draft"
  | "AwaitingReview"
  | "Applied"
  | "Cancelled"
  | "Failed";

export type ApplyOutcome = "Inserted" | "Updated" | "SkippedKept" | "SkippedConflict";

export interface RowOutcome {
  tableCode: string;
  keyValue: string | null;
  outcome: ApplyOutcome;
  isCustom: boolean;
}

export interface CustomFieldError {
  tableCode: string;
  keyValue: string | null;
  column: string;
  message: string;
}

export interface ColumnDiff {
  column: string;
  targetValue: string | null;
  sourceValue: string | null;
  differs: boolean;
}

export interface RowDiff {
  tableCode: string;
  keyColumn: string;
  keyValue: string | null;
  changeType: RowChangeType;
  columns: ColumnDiff[];
  sourcePayload: Record<string, string | null>;
  targetValues: Record<string, string | null> | null;
  conflicts: RowConflictType[];
  isApplicable: boolean;
  hasChanges: boolean;
}

export interface TableDiff {
  tableCode: string;
  tableName: string;
  keyColumn: string;
  rows: RowDiff[];
  targetStateHash: string;
  newCount: number;
  changedCount: number;
  unchangedCount: number;
  conflictCount: number;
}

export interface SyncSession {
  structureName: string;
  sourceLabel: string;
  targetLabel: string;
  tables: TableDiff[];
  targetStateHash: string;
  totalApplicableChanges: number;
  totalConflicts: number;
}

export interface GabSyncRunSummary {
  id: string;
  productGuid: string;
  structureName: string;
  status: GabSyncRunStatus;
  targetStateHash: string;
  sourceLabel: string;
  targetLabel: string;
  newCount: number;
  changedCount: number;
  unchangedCount: number;
  conflictCount: number;
  createdOn: string;
  createdBy: string;
  lastUpdatedOn: string;
  lastUpdatedBy: string;
  completedOn: string | null;
  completedBy: string | null;
  appliedInserted: number;
  appliedUpdated: number;
  appliedSkipped: number;
  errorMessage: string | null;
}

export interface RowResolution {
  tableCode: string;
  keyValue: string | null;
  resolution: GabRowResolutionType;
  customPayload?: Record<string, string | null>;
}

export interface GabSyncRunDetail {
  summary: GabSyncRunSummary;
  session: SyncSession;
  resolutions: RowResolution[];
  outcomes: RowOutcome[];
  snapshotTables: string[];
}

// Stable key for a row within a session (table code + merge-key value).
export function rowKey(tableCode: string, keyValue: string | null): string {
  return `${tableCode}::${keyValue ?? ""}`;
}

// Display column holding the human-readable name, per table.
export function nameFieldFor(tableCode: string): string {
  return tableCode === "EB" ? "EB_Name" : "comp_name";
}

// Config-derived defaults for the start-compare screen: the source label plus the name-suffix
// round-trip values (strip the source's suffix, append this environment's), pre-filled and overridable.
export interface GabCompareDefaults {
  sourceLabel: string;
  sourceNameSuffix: string;
  targetNameSuffix: string;
}
